import {
  addDepartment,
  authenticateWebSearchLogin,
  deleteApiUserToken,
  deleteDepartment,
  deleteGroup,
  deleteUser,
  downloadUsersList,
  createApiUserToken,
  forceLogOff,
  generateSigDocLinkGUIDAuthCode,
  getActiveUsers,
  getAllApps,
  getApiUserTokens,
  getConfigurableTokens,
  getDepartmentList,
  getExternalUserList,
  getGroupApps,
  getGroupChildren,
  getGroupList,
  getGroupListEnabledOnly,
  getGroupRoles,
  getGroupTokens,
  getLdapUserList,
  getLdapServerIp,
  getLoginTypes,
  getPortalUserList,
  getSignatureLinkGUIDDetails,
  getUserDetails,
  getUserList,
  getUserListEnabledOnly,
  getUserListEnabledNotExternal,
  getUserListIncludeExternal,
  getUserListNoApi,
  getUserApps,
  getUserRoles,
  getUserTokens,
  importLdapUsers,
  saveGroupApps,
  saveGroupChildren,
  saveGroupDetails,
  saveGroupRoles,
  saveGroupTokens,
  saveUserDetails,
  saveUserApps,
  saveUserRoles,
  saveUserTokens,
  sendLogOffEmail,
  sendWelcomeEmail,
 } from '@/store/apiModules/security.api'

 import {
  buildActiveUserTableData,
  buildGroupTableData,
  buildImageTableData,
  buildParentFolderGroupTableData,
  buildParentFolderUserTableData,
  buildWorkflowGroupTableData,
  buildWorkflowUserTableData,
  buildWorkflowPortalGroupTableData,
  buildWorkflowPortalUserTableData,
  buildUserTableData,
  getEllipsisButtonConfig,
 } from '@/store/helperModules/security.module';

 import {
  downloadCSV,
  downloadPDF,
} from '@/store/helperModules/reporting.module'

 function initialState() {
  return {
    activeUserList: [],
    departmentList: [],
    ellipsisButtonConfig: null,
    externalUserList: [],
    groupList: [],
    groupListEnabledOnly: [],
    groupType: 'internal',
    loginTypeList: [],
    portalUserList: [],
    securitySideNav: '',
    userList: [],
  }
}

const security = {
  namespaced: true,
  state: initialState,
  mutations: {
    changeSecuritySideNav(state, value) { state.securitySideNav = value },
    resetStoreData(state) {
      const localState = initialState()
      Object.keys(state).forEach((prop) => {
        state[prop] = localState[prop]
      })
    },
    setActiveUserList(state, value) { state.activeUserList = value },
    setDepartmentList(state, value) { state.departmentList = value },
    setEllipsisButtonConfig(state, value) { state.ellipsisButtonConfig = value },
    setExternalUserList(state, value) { state.externalUserList = value },
    setGroupList(state, value) { state.groupList = value },
    setGroupListEnabledOnly(state, value) { state.groupListEnabledOnly = value },
    setGroupType(state, value) { state.groupType = value },
    setLoginTypeList(state, value) { state.loginTypeList = value },
    setPortalUserList(state, value) { state.portalUserList = value },
    setUserList(state, value) { state.userList = value },
  },
  actions: {
    async addDepartment({ rootState }, payload) {
      return await addDepartment(rootState.common.apiToken, payload)
    },
    async authenticateWebSearchLogin({ rootState, dispatch }, payload) {
      const loginResult = await authenticateWebSearchLogin(rootState.common.apiToken, payload)
      if (loginResult && loginResult.Token) {
        await dispatch('common/setUserDataWithAuthInfo', loginResult, { root: true })
      }
      return loginResult
    },
    changeSecuritySideNav({ commit }, value) {
      commit('changeSecuritySideNav', value)
    },
    async deleteApiUserToken({ rootState }, apiUserTokenId) {
      const result = await deleteApiUserToken(rootState.common.apiToken, apiUserTokenId)
      return result
    },
    async deleteDepartment({ rootState }, payload) {
      return await deleteDepartment(rootState.common.apiToken, payload)
    },
    async deleteGroup({ rootState }, groupID) {
      const result = await deleteGroup(rootState.common.apiToken, groupID)
      return result
    },
    async deleteUser({ rootState }, userID) {
      const result = await deleteUser(rootState.common.apiToken, userID)
      return result
    },
    async downloadUsersList({ rootState }, payload) {
      const result = await downloadUsersList(rootState.common.apiToken, payload)
      if (payload?.outputFormat === "CSV") downloadCSV(result, `${payload?.reportType} Users List`)
      if (payload?.outputFormat === "PDF") downloadPDF(result, `${payload?.reportType} Users List`)
    },
    async createApiUserToken({ rootState }, payload) {
      const result = await createApiUserToken(rootState.common.apiToken, payload)
      return result
    },
    async forceLogOff({ rootState }, payload) {
      const result = await forceLogOff(rootState.common.apiToken, payload)
      return result
    },
    async generateSigDocLinkGUIDAuthCode({ rootState }, payload) {
      const result = await generateSigDocLinkGUIDAuthCode(rootState.common.apiToken, payload)
      return result
    },
    async getActiveUsers({ rootState, commit }) {
      let activeUserList = await getActiveUsers(rootState.common.apiToken)
      if (!activeUserList) {
        activeUserList = []
      }

      commit('setActiveUserList', activeUserList)
    },
    async getAllApps({ rootState }, value) {
      const appList = await getAllApps(rootState.common.apiToken, value)
      return appList
    },
    async getApiUserTokens({ rootState }, userID) {
      const userDetails = await getApiUserTokens(rootState.common.apiToken, userID)
      return userDetails
    },
    async getConfigurableTokens({ rootState }) {
      const tokensResult = await getConfigurableTokens(rootState.common.apiToken)
      return tokensResult
    },
    async getGroupApps({ rootState }, groupID) {
      const rolesResult = await getGroupApps(rootState.common.apiToken, groupID)
      return rolesResult
    },
    async getGroupChildren({ rootState }, groupID) {
      const childrenResult = await getGroupChildren(rootState.common.apiToken, groupID)
      return childrenResult
    },
    async getGroupRoles({ rootState }, groupID) {
      const rolesResult = await getGroupRoles(rootState.common.apiToken, groupID)
      return rolesResult
    },
    async getGroupTokens({ rootState }, groupID) {
      const tokensResult = await getGroupTokens(rootState.common.apiToken, groupID)
      return tokensResult
    },
    async getSignatureLinkGUIDDetails({ rootState, dispatch }, payload) {
      const signatureLinkGUIDResult = await getSignatureLinkGUIDDetails(rootState.common.apiToken, payload)
      if (signatureLinkGUIDResult && signatureLinkGUIDResult.Token) {
        await dispatch('common/signInSignatureDocLinkView', signatureLinkGUIDResult.Token, { root: true })
      }
      return signatureLinkGUIDResult
    },
    async getUserApps({ rootState }, userID) {
      const rolesResult = await getUserApps(rootState.common.apiToken, userID)
      return rolesResult
    },
    async getUserDetails({ rootState }, userID) {
      const userDetails = await getUserDetails(rootState.common.apiToken, userID)
      return userDetails
    },
    async getUserListEnabledOnly({ rootState }) {
      const userListResult = await getUserListEnabledOnly(rootState.common.apiToken)
      return userListResult
    },
    async getUserListEnabledNotExternal({ rootState }) {
      const userListResult = await getUserListEnabledNotExternal(rootState.common.apiToken)
      return userListResult
    },
    async getUserListIncludeExternal({ rootState }) {
      const userListResult = await getUserListIncludeExternal(rootState.common.apiToken)
      return userListResult
    },
    async getUserRoles({ rootState }, userID) {
      const rolesResult = await getUserRoles(rootState.common.apiToken, userID)
      return rolesResult
    },
    async getUserTokens({ rootState }, userID) {
      const tokensResult = await getUserTokens(rootState.common.apiToken, userID)
      return tokensResult
    },
    async loadDepartmentList({ rootState, commit }) {
      let departmentList = await getDepartmentList(rootState.common.apiToken)
      if (!departmentList) {
        departmentList = []
      }

      commit('setDepartmentList', departmentList)
    },
    async loadExternalUsersList({ rootState, commit }) {
      let externalUserList = await getExternalUserList(rootState.common.apiToken)
      if (!externalUserList) {
        externalUserList = []
      }

      commit('setExternalUserList', externalUserList)
    },
    async loadGroupList({ rootState, commit }) {
      let groupList = await getGroupList(rootState.common.apiToken)
      if (!groupList) {
        groupList = []
      }

      commit('setGroupList', groupList)
    },
    async loadGroupListEnabledOnly({ rootState, commit }) {
      let groupList = await getGroupListEnabledOnly(rootState.common.apiToken)
      if (!groupList) {
        groupList = []
      }

      commit('setGroupListEnabledOnly', groupList)
    },
    async loadLoginTypeList({ rootState, commit }) {
      let loginTypeList = await getLoginTypes(rootState.common.apiToken)
      if (!loginTypeList) {
        loginTypeList = []
      }

      commit('setLoginTypeList', loginTypeList)
    },
    async loadPortalUsersList({ rootState, commit }) {
      let portalUserList = await getPortalUserList(rootState.common.apiToken)
      if (!portalUserList) {
        portalUserList = []
      }

      commit('setPortalUserList', portalUserList)
    },    
    async getLdapUserList({ rootState }, payload) {
       return await getLdapUserList(rootState.common.apiToken, payload)
    },
    async getLdapServerIp({ rootState }) {
      return await getLdapServerIp(rootState.common.apiToken)
   },
    async importLdapUsers({ rootState }, payload) {
      return await importLdapUsers(rootState.common.apiToken, payload)
   },
    async loadUserList({ rootState, commit }) {
      let userList = await getUserList(rootState.common.apiToken)
      if (!userList) {
        userList = []
      }

      commit('setUserList', userList)
    },
    async loadUserListNoApi({ rootState, commit }) {
      let userList = await getUserListNoApi(rootState.common.apiToken)
      if (!userList) {
        userList = []
      }

      commit('setUserList', userList)
    },
    async saveGroupApps({ rootState }, payload) {
      const result = await saveGroupApps(rootState.common.apiToken, payload)
      return result
    },
    async saveGroupChildren({ rootState }, payload) {
      const result = await saveGroupChildren(rootState.common.apiToken, payload)
      return result
    },
    async saveGroupDetails({ rootState }, payload) {
      const result = await saveGroupDetails(rootState.common.apiToken, payload)
      return result
    },
    async saveGroupRoles({ rootState }, payload) {
      const result = await saveGroupRoles(rootState.common.apiToken, payload)
      return result
    },
    async saveGroupTokens({ rootState }, payload) {
      const result = await saveGroupTokens(rootState.common.apiToken, payload)
      return result
    },
    async saveUserApps({ rootState }, payload) {
      const result = await saveUserApps(rootState.common.apiToken, payload)
      return result
    },
    async saveUserDetails({ rootState }, payload) {
      const result = await saveUserDetails(rootState.common.apiToken, payload)
      return result
    },
    async saveUserRoles({ rootState }, payload) {
      const result = await saveUserRoles(rootState.common.apiToken, payload)
      return result
    },
    async saveUserTokens({ rootState }, payload) {
      const result = await saveUserTokens(rootState.common.apiToken, payload)
      return result
    },
    // eslint-disable-next-line no-empty-pattern
    async setActiveUserTableData({}, data) {
      const response = await buildActiveUserTableData(data)
      return response
    },
    async setEllipsisButtonConfig({ commit }, configName) { commit('setEllipsisButtonConfig', getEllipsisButtonConfig(configName)) },
    // eslint-disable-next-line no-empty-pattern
    async setGroupTableData({}, data) {
      const response = await buildGroupTableData(data)
      return response
    },
    async setGroupType({ commit }, groupType) { commit('setGroupType', groupType) },
    // eslint-disable-next-line no-empty-pattern
    async setImageTableData({}, data) {
      const response = await buildImageTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setParentFolderGroupTableData({}, data) {
      const response = await buildParentFolderGroupTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setParentFolderUserTableData({}, data) {
      const response = await buildParentFolderUserTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setUserTableData({}, data) {
      const response = await buildUserTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setWorkflowGroupTableData({}, data) {
      const response = await buildWorkflowGroupTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setWorkflowPortalGroupTableData({}, data) {
      const response = await buildWorkflowPortalGroupTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setWorkflowPortalUserTableData({}, data) {
      const response = await buildWorkflowPortalUserTableData(data)
      return response
    },
    // eslint-disable-next-line no-empty-pattern
    async setWorkflowUserTableData({}, data) {
      const response = await buildWorkflowUserTableData(data)
      return response
    },
    async sendLogOffEmail({ rootState }, email) {
      const result = await sendLogOffEmail(rootState.common.apiToken, email)
      return result
    },
    async sendWelcomeEmail({ rootState }, userId) {
      const result = await sendWelcomeEmail(rootState.common.apiToken, userId)
      return result
    }
  },
}

export default security;
